/**
 * Global decimal-input normaliser.
 *
 * CleanTask is served in French/Dutch/Portuguese locales where users naturally
 * type "25,50" for decimals, but <input type="number"> follows the HTML5 spec:
 * its DOM `.value` is always "."-separated, and several browsers (Firefox,
 * Safari, some Chrome builds on mixed-locale systems) refuse a comma outright,
 * which yields an empty submission silently.
 *
 * This module makes comma input work reliably on every browser:
 *
 *   1. On `keydown`, `,` and `.` are treated as interchangeable decimal
 *      separators. The `,` keystroke is swallowed and a `.` is inserted in its
 *      place at the caret position BEFORE the browser evaluates validity, so
 *      Firefox never sees the comma.
 *   2. On `input`/`blur`, any residual comma is replaced with a dot.
 *   3. On form `submit` and HTMX `configRequest`, every decimal-capable input
 *      is normalised one last time as a safety net.
 *
 * It targets:
 *   - All <input type="number"> with a non-integer `step` (default `any`).
 *   - Any <input> with `inputmode="decimal"` or the `data-decimal-input` hook.
 *   - The `.form-input` + `type="number"` widgets generated by Django forms.
 *
 * It explicitly EXCLUDES `data-money-input` fields: those display the
 * locale comma (sanitizer in app.js) and are normalised server-side.
 */

const DECIMAL_SELECTOR = [
    'input[type="number"]',
    'input[inputmode="decimal"]',
    'input[data-decimal-input]',
].join(', ');

function isDecimalInput(el) {
    if (!el || el.nodeType !== 1 || el.tagName !== 'INPUT') return false;
    if (el.disabled || el.readOnly) return false;

    // Money fields (``data-money-input``) are owned by the global
    // sanitizer in app.js: they DISPLAY the locale comma and the server
    // normalises it (DecimalTextInput / explicit replace). Normalising
    // them to a dot here would fight that display convention on every
    // keystroke/blur.
    if (el.hasAttribute('data-money-input')) return false;

    const type = (el.getAttribute('type') || '').toLowerCase();
    const inputMode = (el.getAttribute('inputmode') || '').toLowerCase();
    if (el.hasAttribute('data-decimal-input')) return true;
    if (inputMode === 'decimal') return true;

    if (type !== 'number') return false;
    const step = (el.getAttribute('step') || '').trim().toLowerCase();
    if (step === '' || step === 'any') return true;
    if (step.indexOf('.') !== -1 || step.indexOf(',') !== -1) return true;
    const num = parseFloat(step.replace(',', '.'));
    return Number.isFinite(num) && num < 1;
}

function normalizeValue(value) {
    if (typeof value !== 'string' || value === '') return value;
    return value.replace(/,/g, '.');
}

function setNormalizedValue(input, raw) {
    const normalized = normalizeValue(raw);
    if (input.value !== normalized) {
        input.value = normalized;
    }
}

function insertAtCaret(input, text) {
    try {
        const start = input.selectionStart;
        const end = input.selectionEnd;
        if (typeof start === 'number' && typeof end === 'number') {
            const before = input.value.substring(0, start);
            const after = input.value.substring(end);
            input.value = before + text + after;
            const caret = start + text.length;
            input.setSelectionRange(caret, caret);
            return true;
        }
    } catch (err) { /* eslint-disable-line no-unused-vars */
        // `selectionStart`/`setSelectionRange` are not supported on
        // <input type="number"> in some browsers. Fall back to plain append.
    }
    input.value = (input.value || '') + text;
    return false;
}

function handleKeydown(e) {
    if (e.key !== ',' && e.code !== 'Comma' && e.key !== '.') return;
    const input = e.target;
    if (!isDecimalInput(input)) return;

    e.preventDefault();
    const current = input.value || '';

    // Only one decimal separator allowed: if there is already a dot,
    // ignore further comma/dot presses (matches native browser behaviour).
    if (current.indexOf('.') !== -1) return;

    insertAtCaret(input, '.');
    input.dispatchEvent(new Event('input', { bubbles: true }));
}

function handleInput(e) {
    const input = e.target;
    if (!isDecimalInput(input)) return;
    if (typeof input.value !== 'string') return;
    if (input.value.indexOf(',') === -1) return;
    setNormalizedValue(input, input.value);
}

function handleBlur(e) {
    const input = e.target;
    if (!isDecimalInput(input)) return;
    if (typeof input.value !== 'string') return;
    setNormalizedValue(input, input.value);
}

function normalizeFormOnSubmit(e) {
    const form = e.target;
    if (!form || !form.querySelectorAll) return;
    form.querySelectorAll(DECIMAL_SELECTOR).forEach(function (input) {
        if (isDecimalInput(input)) setNormalizedValue(input, input.value);
    });
}

function normalizeHtmxPayload(evt) {
    const elt = evt.detail && evt.detail.elt;
    if (!elt) return;
    const form = elt.form || (elt.closest ? elt.closest('form') : null) || elt;
    if (form && form.querySelectorAll) {
        form.querySelectorAll(DECIMAL_SELECTOR).forEach(function (input) {
            if (isDecimalInput(input) && typeof input.value === 'string') {
                setNormalizedValue(input, input.value);
            }
        });
    }
    const params = evt.detail && evt.detail.parameters;
    if (params && typeof params === 'object') {
        Object.keys(params).forEach(function (key) {
            const val = params[key];
            if (typeof val === 'string' && val.indexOf(',') !== -1) {
                // Heuristic: only normalise strings that look like a decimal
                // number with a comma — don't mangle descriptions or names.
                if (/^-?\d+,\d+$/.test(val.trim())) {
                    params[key] = val.replace(',', '.');
                }
            }
        });
    }
}

/**
 * Wire up the global listeners. Idempotent — safe to call more than once,
 * the flag guards against double-registration.
 */
export function initDecimalInputs() {
    if (window.__decimalInputInitDone) return;
    window.__decimalInputInitDone = true;

    document.addEventListener('keydown', handleKeydown, true);
    document.addEventListener('input', handleInput, true);
    document.addEventListener('blur', handleBlur, true);
    document.addEventListener('submit', normalizeFormOnSubmit, true);
    document.body.addEventListener('htmx:configRequest', normalizeHtmxPayload);
}

export default initDecimalInputs;
